import { useState } from 'react'
import {
  Card, Row, Col, Table, Button, Select, DatePicker, Space, Typography,
  Alert, Divider,
} from 'antd'
import { SearchOutlined, PrinterOutlined, WarningOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs, { Dayjs } from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

const fmtAmt = (v: number) =>
  Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

interface BSLineItem {
  account: string
  group_type: string
  amount: number
}

function buildTableData(items: BSLineItem[], startKey = 0) {
  return items.map((r, i) => ({ ...r, key: startKey + i }))
}

function subtotal(items: BSLineItem[]) {
  return items.reduce((acc, r) => acc + (r.amount ?? 0), 0)
}

const lineColumns = [
  {
    title: 'Account',
    dataIndex: 'account',
    key: 'account',
    render: (v: string) => <Text style={{ fontSize: 13 }}>{v}</Text>,
  },
  {
    title: 'Amount (₹)',
    dataIndex: 'amount',
    key: 'amount',
    align: 'right' as const,
    width: 150,
    render: (v: number) => (
      <Text style={{ fontFamily: 'monospace', fontSize: 13 }}>
        {fmtAmt(v)}
      </Text>
    ),
  },
]

interface SectionTableProps {
  title: string
  titleColor: string
  items: BSLineItem[]
  subtotalLabel: string
  subtotalColor: string
  loading: boolean
  startKey?: number
}

function SectionTable({
  title, titleColor, items, subtotalLabel, subtotalColor, loading, startKey = 0,
}: SectionTableProps) {
  const total = subtotal(items)
  return (
    <Card
      size="small"
      style={{ marginBottom: 12 }}
      title={<Text strong style={{ color: titleColor, fontSize: 13 }}>{title}</Text>}
    >
      <Table
        dataSource={buildTableData(items, startKey)}
        columns={lineColumns}
        pagination={false}
        size="small"
        loading={loading}
        locale={{ emptyText: 'No data' }}
        summary={() => (
          <Table.Summary.Row style={{ background: '#fafafa' }}>
            <Table.Summary.Cell index={0}>
              <Text strong>{subtotalLabel}</Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} align="right">
              <Text strong style={{ fontFamily: 'monospace', color: subtotalColor }}>
                {fmtAmt(total)}
              </Text>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />
    </Card>
  )
}

export default function BalanceSheetPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const [asOfDate, setAsOfDate] = useState<Dayjs>(dayjs())
  const [enabled, setEnabled] = useState(false)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['mis-balance-sheet', plantId, asOfDate?.format('YYYY-MM-DD')],
    queryFn: () =>
      apiClient.get('/mis/balance-sheet', {
        params: {
          plant_id: plantId,
          as_of_date: asOfDate?.format('YYYY-MM-DD'),
        },
      }).then(r => r.data),
    enabled: enabled && !!plantId,
  })

  const handleGenerate = () => {
    setEnabled(true)
    refetch()
  }

  const assets: BSLineItem[] = data?.assets ?? []
  const liabilities: BSLineItem[] = data?.liabilities ?? []
  const equity: BSLineItem[] = data?.equity ?? []

  const totalAssets = data?.total_assets ?? subtotal(assets)
  const totalLiabEquity = data?.total_liabilities_equity ?? (subtotal(liabilities) + subtotal(equity))

  const isBalanced = data
    ? Math.abs(totalAssets - totalLiabEquity) < 0.01
    : true

  return (
    <div style={{ padding: 24 }}>
      <PageHeader
        title="Balance Sheet"
        subtitle="Financial position as of a specific date"
        extra={
          <Button icon={<PrinterOutlined />} disabled={!data}>
            Print / Export
          </Button>
        }
      />

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            style={{ width: 200 }}
            placeholder="Select Plant"
            value={plantId || undefined}
            onChange={v => { setPlantId(v); setEnabled(false) }}
            options={plants.map((p: any) => ({ label: p.name, value: p.id }))}
          />
          <DatePicker
            placeholder="As of Date"
            value={asOfDate}
            onChange={v => { setAsOfDate(v!); setEnabled(false) }}
            format="DD-MMM-YYYY"
            allowClear={false}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            loading={isLoading}
            onClick={handleGenerate}
          >
            Generate
          </Button>
        </Space>
      </Card>

      {/* Balance mismatch warning */}
      {data && !isBalanced && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message="Balance sheet does not balance"
          description={`Total Assets (₹${fmtAmt(totalAssets)}) ≠ Total Liabilities & Equity (₹${fmtAmt(totalLiabEquity)}). Please review your chart of accounts.`}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Two-column layout */}
      <Row gutter={16} align="top">
        {/* Left — Assets */}
        <Col xs={24} lg={12}>
          <SectionTable
            title="ASSETS"
            titleColor="#1677ff"
            items={assets}
            subtotalLabel="Total Assets"
            subtotalColor="#1677ff"
            loading={isLoading}
            startKey={0}
          />

          {data && (
            <Card size="small" style={{ background: '#e6f4ff', border: '1px solid #91caff' }}>
              <Row justify="space-between">
                <Text strong>TOTAL ASSETS</Text>
                <Text strong style={{ fontFamily: 'monospace', color: '#1677ff', fontSize: 15 }}>
                  ₹ {fmtAmt(totalAssets)}
                </Text>
              </Row>
            </Card>
          )}
        </Col>

        {/* Right — Liabilities + Equity */}
        <Col xs={24} lg={12}>
          <SectionTable
            title="LIABILITIES"
            titleColor="#ff4d4f"
            items={liabilities}
            subtotalLabel="Total Liabilities"
            subtotalColor="#ff4d4f"
            loading={isLoading}
            startKey={1000}
          />

          <Divider style={{ margin: '8px 0' }} />

          <SectionTable
            title="EQUITY"
            titleColor="#722ed1"
            items={equity}
            subtotalLabel="Total Equity"
            subtotalColor="#722ed1"
            loading={isLoading}
            startKey={2000}
          />

          {data && (
            <Card
              size="small"
              style={{
                background: isBalanced ? '#f6ffed' : '#fff2f0',
                border: `1px solid ${isBalanced ? '#b7eb8f' : '#ffccc7'}`,
              }}
            >
              <Row justify="space-between">
                <Text strong>TOTAL LIABILITIES & EQUITY</Text>
                <Text
                  strong
                  style={{
                    fontFamily: 'monospace',
                    color: isBalanced ? '#52c41a' : '#ff4d4f',
                    fontSize: 15,
                  }}
                >
                  ₹ {fmtAmt(totalLiabEquity)}
                </Text>
              </Row>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  )
}
